/**
 * Reading the family envelope.
 *
 * The balance is computed here, in the client, by `projectEnvelope` over the
 * entries — not read from a column. That is the same rule as every other
 * balance in the product: a stored counter is a number that can disagree with
 * its own history, and the moment it does, nobody can tell which one lied.
 *
 * Issuance is the opposite: refills are inserted server-side. A client that
 * could write refill rows could top up the family's fun money by editing a
 * date, so this module only ever reads them.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase.js';
import {
  ENVELOPE_DEFAULTS, approveProposal, createProposal, declineProposal,
  envelopeReversalEntry, projectEnvelope, projectGoal, spendEntry,
} from '../domain/envelope.js';
import { toDateOnly } from '../domain/periods.js';

export function useFuel(familyId) {
  const [state, setState] = useState({
    loading: true, envelope: null, rule: null, goal: null, projection: null,
    tiles: [], allTiles: [], entries: [], proposals: [],
  });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!familyId) { setState((s) => ({ ...s, loading: false })); return; }

    const [envelopeRes, goalRes] = await Promise.all([
      supabase.from('family_envelopes').select('id, name').eq('family_id', familyId).is('archived_at', null).maybeSingle(),
      supabase.from('family_goals').select('id, name, target_cents').eq('family_id', familyId).is('achieved_at', null).order('created_at').limit(1).maybeSingle(),
    ]);

    if (envelopeRes.error) { setError(envelopeRes.error.message); setState((s) => ({ ...s, loading: false })); return; }
    const envelope = envelopeRes.data;
    if (!envelope) {
      setState({
        loading: false, envelope: null, rule: null, goal: null, projection: null,
        tiles: [], allTiles: [], entries: [], proposals: [],
      });
      return;
    }

    const [ruleRes, entryRes, tileRes, proposalRes] = await Promise.all([
      supabase.from('envelope_rules').select('version, cadence, amount_cents, rollover_cap_cents, requires_second')
        .eq('envelope_id', envelope.id).order('effective_from', { ascending: false }).limit(1).maybeSingle(),
      // `reason_id`, `tile_id`, `note` and `actor_id` are pulled in alongside
      // the columns the gauge alone needs, because the history screen reads
      // the same rows: a reversal's reason lives in `note`, which tile it was
      // (or wasn't) is `tile_id`, and a reversal's `reason_id` is the id of
      // the entry it corrects — mapped below to `reversesId` so it matches
      // the shape `envelopeReversalEntry` produces.
      supabase.from('envelope_entries')
        .select('id, currency, amount_cents, reason_type, reason_id, goal_id, ledger, tile_id, note, actor_id, occurred_at')
        .eq('envelope_id', envelope.id)
        .order('occurred_at', { ascending: true })
        .order('created_at', { ascending: true }),
      // Archived tiles come back too. A tile is archived rather than deleted
      // precisely so history keeps reading "Ice cream" instead of "Spend"
      // forever after, and that only works if something can still look the
      // name up — so the filter moved out of SQL and into the split below.
      supabase.from('envelope_tiles').select('id, label, default_amount_cents, icon, sort_order, archived_at')
        .eq('envelope_id', envelope.id).order('sort_order'),
      supabase.from('spend_proposals')
        .select('id, family_id, envelope_id, child_id, amount_cents, tile_id, note, status, requires_second, seconded_by, decided_by, counter, created_at')
        .eq('envelope_id', envelope.id)
        .order('created_at', { ascending: false }),
    ]);

    const firstError = ruleRes.error || entryRes.error || tileRes.error || proposalRes.error;
    if (firstError) { setError(firstError.message); setState((s) => ({ ...s, loading: false })); return; }

    // Postgres returns snake_case; the domain speaks camelCase and must not be
    // bent to match a transport. Map at the boundary, once.
    const entries = (entryRes.data ?? []).map((e) => ({
      id: e.id,
      currency: e.currency,
      amountCents: e.amount_cents,
      reasonType: e.reason_type,
      reasonId: e.reason_type === 'reversal' ? undefined : e.reason_id,
      goalId: e.goal_id,
      ledger: e.ledger,
      tileId: e.tile_id,
      note: e.note,
      actorId: e.actor_id,
      occurredAt: e.occurred_at,
      ...(e.reason_type === 'reversal' ? { reversesId: e.reason_id } : {}),
    }));

    const proposals = (proposalRes.data ?? []).map((p) => ({
      id: p.id,
      familyId: p.family_id,
      envelopeId: p.envelope_id,
      childId: p.child_id,
      amountCents: p.amount_cents,
      tileId: p.tile_id,
      note: p.note,
      status: p.status,
      requiresSecond: p.requires_second,
      secondedBy: p.seconded_by,
      decidedBy: p.decided_by,
      counter: p.counter,
      createdAt: p.created_at,
    }));

    const allTiles = (tileRes.data ?? []).map((t) => ({
      id: t.id,
      label: t.label,
      defaultAmountCents: t.default_amount_cents,
      icon: t.icon,
      sortOrder: t.sort_order,
      archivedAt: t.archived_at,
    }));

    const goal = goalRes.data
      ? {
          id: goalRes.data.id,
          name: goalRes.data.name,
          targetCents: goalRes.data.target_cents,
          // Projected from the rollover entries that named it, never stored.
          ...projectGoal(entries, goalRes.data.id),
        }
      : null;

    setState({
      loading: false,
      envelope,
      rule: ruleRes.data && {
        version: ruleRes.data.version,
        cadence: ruleRes.data.cadence,
        amountCents: ruleRes.data.amount_cents,
        rolloverCapCents: ruleRes.data.rollover_cap_cents,
        requiresSecond: ruleRes.data.requires_second,
      },
      goal,
      projection: projectEnvelope(entries),
      // `tiles` is what gets tapped; `allTiles` is what gets named. Two lists
      // rather than one filtered at every call site, because forgetting the
      // filter on the logging screen would offer an archived tile again and
      // forgetting it on the history screen only loses a label.
      tiles: allTiles.filter((t) => !t.archivedAt),
      allTiles,
      entries,
      proposals,
    });
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, error, reload: load, today: toDateOnly(new Date()) };
}

/** Postgres columns from a domain entry. Mapped in one place, both directions. */
const entryRow = (e) => ({
  id: e.id,
  family_id: e.familyId,
  envelope_id: e.envelopeId,
  currency: e.currency,
  amount_cents: e.amountCents,
  reason_type: e.reasonType,
  reason_id: e.reasonId ?? e.reversesId ?? null,
  tile_id: e.tileId ?? null,
  actor_id: e.actorId ?? null,
  note: e.note ?? null,
  snapshot: e.snapshot ?? null,
  occurred_at: e.occurredAt,
});

/**
 * Log a family spend. This is the fifteen-second path, and everything about it
 * is shaped by that: one call, no confirm step, no form.
 *
 * `ON CONFLICT DO NOTHING` (`ignoreDuplicates`) rather than an upsert, because
 * the id is deterministic and first-write-wins is the ledger's rule everywhere
 * else. A double-tap on a tile logs one spend.
 */
export async function logSpend({ familyId, envelopeId, amountCents, tileId, note, actorId, occurredAt }) {
  const entry = spendEntry({
    familyId, envelopeId, amountCents, tileId, note, actorId, occurredAt,
    spendId: crypto.randomUUID(),
  });
  const { error } = await supabase.from('envelope_entries')
    .insert(entryRow(entry), { ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return entry;
}

/**
 * Undo a spend that was just logged.
 *
 * Writes a correction, never a delete — see `envelopeReversalEntry`. The undo
 * is offered for a short window after logging because that is when a mis-tap
 * is a mis-tap; correcting last Tuesday's ice cream is a different act and
 * belongs in the history screen with a reason the family can read.
 */
export async function undoSpend({ entry, actorId, occurredAt, reason = 'Logged by mistake' }) {
  const reversal = envelopeReversalEntry({ original: entry, actorId, reason, occurredAt });
  const { error } = await supabase.from('envelope_entries')
    .insert(entryRow(reversal), { ignoreDuplicates: true });
  if (error) throw new Error(error.message);
  return reversal;
}

/** Postgres columns from a domain proposal. Mapped in one place, both directions. */
const proposalRow = (p) => ({
  id: p.id,
  family_id: p.familyId,
  envelope_id: p.envelopeId,
  child_id: p.childId,
  amount_cents: p.amountCents,
  tile_id: p.tileId ?? null,
  note: p.note ?? null,
  status: p.status,
  requires_second: p.requiresSecond,
  seconded_by: p.secondedBy ?? null,
  decided_by: p.decidedBy ?? null,
  counter: p.counter ?? null,
});

/**
 * A child files an ask against the family envelope.
 *
 * Filing moves no money — see `spendImpact` in the ask flow and CLAUDE.md's
 * "Asking moves no money; declining moves no money." `requiresSecond` is
 * snapshotted here, off the rule as it stands right now, so a parent flipping
 * it later never changes the rules under a request already in flight.
 */
export async function createSpendProposal({ familyId, envelopeId, childId, amountCents, tileId, note, requiresSecond = false }) {
  const proposal = createProposal({
    id: crypto.randomUUID(), familyId, envelopeId, childId, amountCents, tileId, note, requiresSecond,
  });
  const { error } = await supabase.from('spend_proposals').insert(proposalRow(proposal));
  if (error) throw new Error(error.message);
  return proposal;
}

/**
 * Approving is what spends the money: one entry, keyed on the proposal id, so
 * approving twice cannot pay twice.
 *
 * The domain's `approveProposal` throws on a proposal that isn't pending —
 * that's the same guard as the ledger's `ignoreDuplicates` insert, just one
 * layer up, and it's what stops a second, stale "Yes" tap (another parent's
 * tab that hasn't reloaded) from re-spending: the insert lands harmlessly on
 * the same primary key, and the status update is idempotent too.
 */
export async function approveSpendProposal({ proposal, actorId, occurredAt }) {
  const approved = approveProposal(proposal, actorId);
  const entry = spendEntry({
    familyId: proposal.familyId,
    envelopeId: proposal.envelopeId,
    spendId: proposal.id,
    amountCents: proposal.amountCents,
    tileId: proposal.tileId,
    note: proposal.note,
    actorId,
    occurredAt,
  });
  const { error: entryErr } = await supabase.from('envelope_entries')
    .insert(entryRow(entry), { ignoreDuplicates: true });
  if (entryErr) throw new Error(entryErr.message);

  const { error: propErr } = await supabase.from('spend_proposals')
    .update({ status: approved.status, decided_by: approved.decidedBy, decided_at: new Date().toISOString() })
    .eq('id', proposal.id);
  if (propErr) throw new Error(propErr.message);
  return approved;
}

/** Declining moves no money — only the proposal's status changes. */
export async function declineSpendProposal({ proposal, actorId, reason = 'Not this time' }) {
  const declined = declineProposal(proposal, actorId, reason);
  const { error } = await supabase.from('spend_proposals')
    .update({ status: declined.status, decided_by: declined.decidedBy, note: declined.note, decided_at: new Date().toISOString() })
    .eq('id', proposal.id);
  if (error) throw new Error(error.message);
  return declined;
}

/**
 * The tiles a family actually uses drift away from the five it started with —
 * a swimming lesson replaces a cinema trip, an ice cream costs $6.50 now — and
 * a set of defaults nobody can correct stops matching what gets bought, which
 * is the same failure as a stale gauge one step upstream.
 *
 * Scoped by id alone: RLS is the only family-scoping mechanism (CLAUDE.md),
 * and re-stating the family in a `.eq()` here would read as the check when it
 * is only a comment on one.
 */
export async function addTile({ familyId, envelopeId, label, defaultAmountCents, icon, sortOrder }) {
  const { data, error } = await supabase.from('envelope_tiles').insert({
    envelope_id: envelopeId,
    family_id: familyId,
    label,
    default_amount_cents: defaultAmountCents,
    icon: icon ?? null,
    sort_order: sortOrder,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return data;
}

/** Only the keys handed in are written, so a reorder can't blank a label. */
export async function updateTile(id, { label, defaultAmountCents, icon, sortOrder }) {
  const patch = {};
  if (label !== undefined) patch.label = label;
  if (defaultAmountCents !== undefined) patch.default_amount_cents = defaultAmountCents;
  if (icon !== undefined) patch.icon = icon;
  if (sortOrder !== undefined) patch.sort_order = sortOrder;
  const { data, error } = await supabase.from('envelope_tiles').update(patch).eq('id', id).select('id');
  if (error) throw new Error(error.message);
  // RLS answers a forbidden update with zero rows, not an error. Without this
  // check a missing policy (migration 0006 not applied) looks like success.
  if (!data?.length) throw new Error('That tile could not be changed');
}

/**
 * Retiring a tile, never deleting one.
 *
 * Every spend logged through it points at this row, and the history screen
 * reads the name from here — so a delete would quietly rewrite three months of
 * "Ice cream" into "Spend". There is no delete policy on the table for the
 * same reason; archiving is the only way out.
 */
export async function archiveTile(id) {
  const { data, error } = await supabase.from('envelope_tiles')
    .update({ archived_at: new Date().toISOString() }).eq('id', id).select('id');
  if (error) throw new Error(error.message);
  if (!data?.length) throw new Error('That tile could not be archived');
}

/**
 * Stand up the whole envelope in one go: the pot, its refill rule, the tiles,
 * and optionally the goal that rollover feeds.
 *
 * Deliberately one call. A parent who has to visit four screens to configure a
 * budget configures it once and never revisits it, and a half-configured
 * envelope — a pot with no tiles — fails the fifteen-second test on every
 * subsequent use.
 */
export async function createFuel({ familyId, amountCents, cadence, rolloverCapCents, tiles, goal }) {
  const { data: envelope, error: eErr } = await supabase
    .from('family_envelopes').insert({ family_id: familyId }).select('id, name').single();
  if (eErr) throw new Error(eErr.message);

  const { error: rErr } = await supabase.from('envelope_rules').insert({
    envelope_id: envelope.id,
    family_id: familyId,
    version: ENVELOPE_DEFAULTS.version,
    cadence,
    amount_cents: amountCents,
    rollover_cap_cents: rolloverCapCents,
    // Off by default. It earns its way on when a parent recognises the problem
    // it solves; see the proposal machine in domain/envelope.js.
    requires_second: false,
  });
  if (rErr) throw new Error(rErr.message);

  if (tiles?.length) {
    const { error: tErr } = await supabase.from('envelope_tiles').insert(
      tiles.map((t, i) => ({
        envelope_id: envelope.id,
        family_id: familyId,
        label: t.label,
        default_amount_cents: t.defaultAmountCents,
        sort_order: i,
      })),
    );
    if (tErr) throw new Error(tErr.message);
  }

  if (goal?.name) {
    const { error: gErr } = await supabase.from('family_goals').insert({
      family_id: familyId, name: goal.name, target_cents: goal.targetCents,
    });
    if (gErr) throw new Error(gErr.message);
  }

  return envelope;
}
